import { useState, useCallback, useEffect, useLayoutEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type RefObject } from "react";
import { PlayerClock } from "./PlayerClock.js";
import { SleepRemaining } from "./SleepRemaining.js";
import { useTrackMemoryRestore } from "./useTrackMemory.js";
import { Button, LiveBadge, useBackLayer } from "@hybrid-iptv/ui";
import { usePlayerStore, type RecentChannel } from "./playerStore.js";
import { arrowsBelongToBar, onZapName } from "./zapRule.js";
import { FullscreenInfo } from "./FullscreenInfo.js";
import { nowInfoFor, type NowInfo } from "../epg/nowInfo.js";
import { ZapBanner } from "./ZapBanner.js";
import { showRemoteNote } from "../remote/remoteNote.js";
import { PlayerSettingsMenu } from "./PlayerSettingsMenu.js";
import { usePlayerSettings } from "./playerSettingsStore.js";
import { effectiveDuration } from "./duration.js";
import { afterPipCloses, placementOf, resolveNowhere } from "./placement.js";
import { useSaveProgress } from "../library/useSaveProgress.js";
import { multiviewChannelFromCurrentPlayer, useMultiviewStore } from "./multiviewStore.js";
import { useRecording } from "./useRecording.js";
import { useRecordRequest } from "./recordRequest.js";
import { runsOnDevice } from "../api/client.js";
import { useRemoteActions, runRemoteAction } from "../remote/remoteActions.js";
import { SEEK_BACK_S, SEEK_FORWARD_S } from "./seekSteps.js";
import { AT_LIVE_S, formatBehind, shiftLiveBy } from "./liveShift.js";
import { FullscreenZap } from "./FullscreenZap.js";
import { useEpgSettings } from "../epg/settingsStore.js";
import { nativeAudioDelay, nativeFrameRateMatch, nativePip, nativePlayerBridge, nativePlayerCapabilities, nativeTimeshift, nativeTunneling, type TimeshiftWindow } from "@hybrid-iptv/player";
import { tileLetter } from "../library/tileLetter.js";
import { useFeatureUnlocked } from "../library/proFeatures.js";
import { UpNext } from "./UpNext.js";

interface PlayerSurfaceProps {
  /** Video element ref owned by PlayerProvider (always mounted, above router). */
  videoRef: RefObject<HTMLVideoElement | null>;
}

/** Hide or restore Android's status and navigation bars (Capacitor's SystemBars). */
function setImmersive(on: boolean): void {
  const cap = (globalThis as { Capacitor?: { nativePromise?: (plugin: string, method: string, options: object) => Promise<unknown> } })
    .Capacitor;
  if (!runsOnDevice() || typeof cap?.nativePromise !== "function") return;
  void cap.nativePromise("SystemBars", on ? "hide" : "show", {}).catch(() => undefined);
}

/**
 * Full screen for the SURFACE (video + dock + strip), not just the video
 * element — so the transport controls and the recent-channels strip stay
 * visible and usable, like a native TV app. Returns the current state so the
 * button can reflect it.
 *
 * Two ways in. The browser's Fullscreen API on the website. In the APK the
 * surface covers the whole page itself (app.css, .player-surface--fullscreen)
 * and Android's system bars hide: the WebView's own element full screen draws
 * the page in a layer above the native picture, hiding it. A history entry
 * makes the phone's Back button leave full screen instead of leaving the page.
 */
function useFullscreen(ref: RefObject<HTMLElement | null>): [boolean, () => void] {
  const [browserFs, setBrowserFs] = useState(false);
  const [appFs, setAppFs] = useState(false);
  // What had focus when full screen opened — a Search result, a Home tile — to
  // hand it back on the way out. Nothing did: Back from a channel started in
  // Search left focus on <body>, nothing highlighted (projector, 2026-09-29).
  const openerRef = useRef<HTMLElement | null>(null);
  // On the website the page is filled AND the browser asked for its full
  // screen; leaving either one (Esc, Back, the button) leaves both.
  const pairedRef = useRef(false);

  useEffect(() => {
    const onFsChange = () => {
      setBrowserFs(!!document.fullscreenElement);
      if (!document.fullscreenElement && pairedRef.current) {
        pairedRef.current = false;
        setAppFs(false);
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // App full screen: page-level class, system bars, and Back to leave. The
  // back stack owns the history entry (@hybrid-iptv/ui backStack) — the menu and the
  // multiview grid register on top of it, and Back unwinds them in order.
  useBackLayer(appFs, () => setAppFs(false));
  // The same release as below, before the first full-screen frame is painted. The effect below runs
  // after paint, and while a film was starting on the onn box that left focus on the Play button
  // under the picture for about a second (nav audit, fullscreen-film step 2, 2026-10-20).
  useLayoutEffect(() => {
    if (appFs) releaseFocusBehind(ref.current);
  }, [appFs, ref]);
  useEffect(() => {
    if (!appFs) {
      if (pairedRef.current) {
        pairedRef.current = false;
        if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
      }
      return;
    }
    const root = document.documentElement;
    root.classList.add("player-fullscreen");
    setImmersive(true);
    // Focus never sits behind the picture. After Go Live it stayed on the
    // guide row under the video, so the first presses moved an invisible ring
    // and it took an extra OK to reach the bar (projector, 2026-09-29). A
    // closing card hands focus back to its row a moment later, so this keeps
    // watching while full screen lasts. Popups above everything are allowed.
    const release = () => releaseFocusBehind(ref.current);
    release();
    document.addEventListener("focusin", release);
    return () => {
      document.removeEventListener("focusin", release);
      root.classList.remove("player-fullscreen");
      setImmersive(false);
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener) requestAnimationFrame(() => returnFocus(opener));
      // Opened from the guide's preview window (OK on it): Back lands on the
      // window again. The mark is read by the guide as full screen ends, so it
      // goes only after that, with the next frame.
      // The guide's full-screen button marks itself the same way.
      const marked = document.querySelector<HTMLElement>("[data-fs-opener]");
      if (marked) {
        requestAnimationFrame(() => {
          marked.removeAttribute("data-fs-opener");
          if (!marked.isConnected) return;
          if (marked.hasAttribute("data-preview-stop") || marked.tabIndex >= 0) marked.focus();
        });
      }
    };
  }, [appFs, ref]);

  const toggle = useCallback(() => {
    const element = ref.current;
    // Never the browser's full screen inside the APK: the WebView does offer
    // it, but it moves the page into a separate full-screen layer ABOVE the
    // native picture, which then shows black (seen on a phone).
    const browserCan = !runsOnDevice() && document.fullscreenEnabled && typeof element?.requestFullscreen === "function";
    const fillPage = () => {
      const active = document.activeElement;
      openerRef.current = active instanceof HTMLElement && active !== document.body && !element?.contains(active) ? active : null;
      setAppFs(true);
    };
    if (appFs) {
      // The back stack consumes the entry it pushed, so a later Back cannot
      // land on it. Leaving the page fill leaves the browser's full screen too.
      setAppFs(false);
    } else if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      // Fill the page AT ONCE, the way the box does, then ask the browser for
      // its full screen on top. Waiting on the browser instead showed a small
      // window while it decided, and a browser that refused or never answered
      // (the click that started a film can be too long ago once its stream is
      // ready) left the video nowhere: it stopped after the arriving grace,
      // taking any error message with it (2026-10-07). A refusal is fine now —
      // the page is already filled.
      fillPage();
      if (browserCan) {
        pairedRef.current = true;
        element.requestFullscreen().catch(() => undefined);
      }
    }
  }, [ref, appFs]);

  return [browserFs || appFs, toggle];
}

/**
 * Back on the page after full screen: focus returns to what opened it, when
 * nothing else has taken focus and it is still on screen. Not with the guide
 * on the page: its rows are recycled as it scrolls, and it puts its own ring
 * back (epg/guideFocus.ts), on the channel now playing.
 */
function returnFocus(opener: HTMLElement): void {
  const active = document.activeElement;
  if (active && active !== document.body) return;
  if (document.querySelector('[role="dialog"], [role="menu"], [role="alertdialog"]')) return;
  if (document.querySelector("[data-guide]")) return;
  if (!opener.isConnected || opener.getClientRects().length === 0) return;
  opener.focus();
}

/**
 * How long a request to fill the screen may be in flight before the placement
 * table stops waiting for it. Long enough for a browser to confirm its own
 * full screen; short enough that a refused request does not leave a video in
 * limbo.
 */
const ARRIVING_MS = 3_000;

/**
 * The always-mounted player surface: a bottom dock that shows the active
 * source with transport controls. Lives above the router so navigating never
 * interrupts the stream. The <video> element must stay mounted for the
 * player's lifecycle, so it renders here even while hidden.
 */
/**
 * A held OK repeats the key, and a button acts on every repeat: held on
 * Previous channel it flipped between two channels sixty times in six seconds
 * (onn box, 2026-10-07). Buttons that change the channel act once per press.
 */
function ignoreHeldOk(event: ReactKeyboardEvent): void {
  if (event.repeat && (event.key === "Enter" || event.key === " ")) event.preventDefault();
}

/** Why a stream did not start, in words a viewer can act on. */
function failureReason(code: string, message: string): string {
  switch (code) {
    case "media":
    case "unsupported":
      return "This device can't play its picture or sound format.";
    case "network":
    case "manifest":
    case "timeout":
      return "The stream didn't answer. Your provider may be busy, or it is no longer offered.";
    case "unavailable":
      // Every retry and every backup address failed. With the provider's HTTP answer
      // the player's own sentence says why (expired, connection limit, gone).
      return /HTTP \d{3}/.test(message) ? message : "The stream didn't answer. Your provider may be busy, or it is no longer offered.";
    default:
      return message;
  }
}

/** Focus never sits behind the full-screen picture; popups above everything keep it. */
function releaseFocusBehind(surface: HTMLElement | null): void {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || active === document.body) return;
  if (surface?.contains(active)) return;
  if (active.closest('[role="dialog"], [role="alertdialog"], [role="menu"], [data-tvkeyboard]')) return;
  active.blur();
}

export function PlayerSurface({ videoRef }: PlayerSurfaceProps) {
  useSaveProgress();
  // Sound and subtitles come back on a live channel the way the viewer left them there.
  useTrackMemoryRestore();
  const source = usePlayerStore((state) => state.source);
  const status = usePlayerStore((state) => state.status);
  const mode = usePlayerStore((state) => state.mode);
  const currentTime = usePlayerStore((state) => state.currentTime);
  const duration = usePlayerStore((state) => state.duration);
  const recentChannels = usePlayerStore((state) => state.recentChannels);
  const previousChannel = usePlayerStore((state) => state.previousChannel);
  const tuneRecent = usePlayerStore((state) => state.tuneRecent);
  const togglePlayPause = usePlayerStore((state) => state.togglePlayPause);
  const seek = usePlayerStore((state) => state.seek);
  const close = usePlayerStore((state) => state.close);
  const playSource = usePlayerStore((state) => state.playSource);
  const error = usePlayerStore((state) => state.error);
  const openMultiview = useMultiviewStore((state) => state.openWith);
  const multiviewActive = useMultiviewStore((state) => state.active);
  const startMultiview = useCallback(() => {
    // Multiview is live-only (each tile is one provider connection).
    if (source?.type !== "live") return;
    const channel = multiviewChannelFromCurrentPlayer();
    if (channel) void openMultiview(channel);
  }, [source?.type, openMultiview]);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [showRecents, setShowRecents] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isFullscreen, toggleFullscreen] = useFullscreen(surfaceRef);
  // Closing the player in full screen ends full screen too: in the APK the
  // page stays hidden behind an app full screen until it is left.
  useEffect(() => {
    if (!source && isFullscreen) toggleFullscreen();
  }, [source, isFullscreen, toggleFullscreen]);
  // Section 14 Phase 2: native Picture-in-Picture (video.requestPictureInPicture)
  // floats the LIVE channel above everything — including fullscreen browse
  // content, which the in-page corner dock cannot do. Feature-detected: hidden
  // on browsers without the API (e.g. Firefox), like the voice-search gate.
  //
  // In the Android app the picture is native, so the whole app shrinks to
  // Android's floating window instead (NativePlayerPlugin) — where the device
  // offers it; most Fire TV devices do not. Leaving the app while a video plays
  // opens it too.
  const [pipActive, setPipActive] = useState(false);
  const [nativePipSupported, setNativePipSupported] = useState(false);
  useEffect(() => {
    const bridge = nativePlayerBridge();
    if (!bridge) return;
    let cancelled = false;
    void nativePlayerCapabilities(bridge).then((capabilities) => {
      if (!cancelled) setNativePipSupported(capabilities.pipSupported);
    });
    const stop = bridge.on("pip", (event) => setPipActive(event.active));
    return () => {
      cancelled = true;
      stop();
    };
  }, []);
  // Picture-in-picture on leaving the app is the viewer's choice, off by
  // default (Settings → Player). The shell starts with it off, so a launch
  // before this runs never floats by surprise.
  const pipOnLeave = usePlayerSettings((state) => state.pipOnLeave);
  useEffect(() => {
    const bridge = nativePlayerBridge();
    if (bridge) void nativePip.setAuto(bridge, pipOnLeave);
  }, [pipOnLeave]);
  // Tunnelled video, the viewer's choice, off by default (Settings → Player).
  const tunneling = usePlayerSettings((state) => state.tunneling);
  useEffect(() => {
    const bridge = nativePlayerBridge();
    if (bridge) void nativeTunneling.set(bridge, tunneling);
  }, [tunneling]);
  // Frame-rate matching and the audio delay, the viewer's choices, off and 0 by default (Settings → Player).
  const frameRateMatch = usePlayerSettings((state) => state.frameRateMatch);
  useEffect(() => {
    const bridge = nativePlayerBridge();
    if (bridge) void nativeFrameRateMatch.set(bridge, frameRateMatch);
  }, [frameRateMatch]);
  const audioDelayMs = usePlayerSettings((state) => state.audioDelayMs);
  useEffect(() => {
    const bridge = nativePlayerBridge();
    if (bridge) void nativeAudioDelay.set(bridge, audioDelayMs);
  }, [audioDelayMs]);

  // Pause and rewind live TV, the viewer's choice, on by default (Settings → Player).
  // Saving live TV to rewind is Pro: a free viewer's buffer stays off whatever the setting says.
  const timeshiftSettingOn = usePlayerSettings((state) => state.timeshift);
  const timeshiftUnlocked = useFeatureUnlocked("timeshift");
  const timeshiftOn = timeshiftSettingOn && timeshiftUnlocked;
  const timeshiftVolumeId = usePlayerSettings((state) => state.timeshiftVolumeId);
  useEffect(() => {
    const bridge = nativePlayerBridge();
    if (bridge) {
      void nativeTimeshift.set(bridge, { enabled: timeshiftOn, ...(timeshiftVolumeId ? { volumeId: timeshiftVolumeId } : {}) });
    }
  }, [timeshiftOn, timeshiftVolumeId]);
  const pipSupported = runsOnDevice()
    ? nativePipSupported
    : typeof document !== "undefined" &&
      "pictureInPictureEnabled" in document &&
      document.pictureInPictureEnabled &&
      typeof videoRef.current?.requestPictureInPicture === "function";

  /**
   * Enter the floating window and REPORT whether it happened.
   *
   * The return value is the whole point (owner 2026-09-21): when a video has to
   * leave the screen it was on, the alternative to picture-in-picture is NOT a
   * smaller player parked on the page — it is no video at all. Silently
   * swallowing a rejected `requestPictureInPicture` (no user gesture, the
   * platform refusing, a device that never had it) is what left a docked band
   * on whatever page the viewer opened next.
   */
  const enterPip = useCallback(async (): Promise<boolean> => {
    const bridge = nativePlayerBridge();
    if (bridge) return await nativePip.enter(bridge);
    const video = videoRef.current;
    if (!video || typeof video.requestPictureInPicture !== "function") return false;
    // Already floating counts as success: asking again would reject with
    // InvalidStateError, and a refusal here is treated as "no PiP available".
    if (document.pictureInPictureElement === video) return true;
    try {
      await video.requestPictureInPicture();
    } catch {
      return false;
    }
    return document.pictureInPictureElement === video;
  }, [videoRef]);

  /**
   * Hand the picture back to the page.
   *
   * The APK floats the WHOLE app, so leaving it is the shell's job: the page
   * asks the plugin (owner 2026-09-21 — the guide reclaims the picture there
   * too, which is what a remote or a voice command needs while the app floats).
   */
  const exitPip = useCallback(async () => {
    const bridge = nativePlayerBridge();
    if (bridge) {
      await nativePip.exit(bridge);
      return;
    }
    if (!document.pictureInPictureElement) return;
    try {
      await document.exitPictureInPicture();
    } catch {
      // Never surface an error for a convenience toggle.
    }
  }, []);

  const togglePip = useCallback(async () => {
    if (!nativePlayerBridge() && document.pictureInPictureElement) {
      await exitPip();
      return;
    }
    await enterPip();
  }, [enterPip, exitPip]);

  // Keep pipActive honest: the browser fires these when the native window is
  // closed by the user or by the OS, so the button label stays in sync even
  // when we did not call exitPictureInPicture ourselves.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onEnter = () => setPipActive(true);
    const onLeave = () => setPipActive(false);
    video.addEventListener("enterpictureinpicture", onEnter);
    video.addEventListener("leavepictureinpicture", onLeave);
    return () => {
      video.removeEventListener("enterpictureinpicture", onEnter);
      video.removeEventListener("leavepictureinpicture", onLeave);
    };
  }, [videoRef]);

  const isLive = source?.type === "live";

  // The gear menu's settings, applied to whichever engine is playing. Aspect
  // is the viewer's standing choice about their screen; speed belongs to what
  // is playing, so a new source starts at normal.
  const playerInstance = usePlayerStore((state) => state.player);
  const aspect = usePlayerSettings((state) => state.aspect);
  const rate = usePlayerSettings((state) => state.rate);
  const setRate = usePlayerSettings((state) => state.setRate);
  const sourceUrl = source?.url;
  // Applied once the picture is actually up, not merely when the source
  // changed: measured on a phone, a new stream started under FIT although the
  // stored choice was FILL, because the engine was still loading when the
  // source change fired.
  const started = status === "playing";
  useEffect(() => {
    if (started) playerInstance?.setAspectMode?.(aspect);
  }, [playerInstance, aspect, sourceUrl, started]);
  useEffect(() => {
    setRate(1);
  }, [sourceUrl, setRate]);
  useEffect(() => {
    if (started) playerInstance?.setPlaybackRate?.(isLive ? 1 : rate);
  }, [playerInstance, rate, isLive, sourceUrl, started]);

  // Section 15: rewind / return-to-live, and recording. Both are live-only.
  const player = usePlayerStore((s) => s.player);
  const [shift, setShift] = useState<TimeshiftWindow>({ available: false, behindSeconds: 0, offsetSeconds: 0 });
  const recording = useRecording(videoRef, source, source?.title ?? null);

  // "Record now" from the guide: the viewer already picked the file; start once
  // the channel is actually playing, so there is a picture to capture.
  const pendingRecording = useRecordRequest((s) => s.pending);
  const playerStatus = usePlayerStore((s) => s.status);
  useEffect(() => {
    if (!pendingRecording || !recording.supported || recording.recording || playerStatus !== "playing") return;
    const request = useRecordRequest.getState().take();
    if (!request) return;
    void recording.start({
      ...(request.sink ? { sink: request.sink } : {}),
      stopAtMs: request.stopAtMs,
      title: request.title,
    });
  }, [pendingRecording, recording, playerStatus]);

  // The retained back buffer grows as the stream plays and shrinks when the
  // browser evicts under memory pressure, so the rewind affordance is polled
  // rather than computed once. Offering a rewind the buffer no longer holds
  // would simply stall.
  useEffect(() => {
    if (!isLive || !player) {
      setShift({ available: false, behindSeconds: 0, offsetSeconds: 0 });
      return;
    }
    const tick = () => setShift(player.timeshift());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isLive, player]);

  // The buffer turned itself off because the drive is too full (ADR 2026-09-27:
  // "auto-off with a message"). Said once per channel; it plays on without rewind.
  const offReason = shift.offReason;
  useEffect(() => {
    if (offReason === "no-space") {
      showRemoteNote("Not enough free space to pause or rewind live TV. A USB drive in Settings → Player gives the full hour.");
    }
  }, [offReason]);

  // Playing something fills the screen, unless it is a preview beside what
  // you are browsing (playerStore's PlaySourceInput.preview). Multiview
  // draws its own grid, so it is left alone.
  const fullscreenWanted = usePlayerStore((state) => state.fullscreenWanted);
  const servedFullscreenRef = useRef(fullscreenWanted);
  useEffect(() => {
    if (fullscreenWanted === servedFullscreenRef.current) return;
    servedFullscreenRef.current = fullscreenWanted;
    if (!isFullscreen && !multiviewActive) toggleFullscreen();
  }, [fullscreenWanted, isFullscreen, multiviewActive, toggleFullscreen]);

  // WHERE THE PICTURE LIVES — one table, not one effect per way of leaving.
  // placementOf() names the state; "nowhere" is a video with no home, and it
  // has a single answer (player/placement.ts, which carries the list of bugs
  // this replaces). Anything that can end a home — leaving full screen,
  // leaving the guide, closing multiview, a refused floating window — arrives
  // here on its own instead of needing an effect somebody remembered to write.
  const previewHost = usePlayerStore((state) => state.previewHost);
  // Asked to fill the screen, not there yet.
  //
  // DERIVED DURING RENDER, and it has to be. The first attempt set this from an
  // effect, and it made no difference at all on the device: `placement` below is
  // computed while rendering, so by the time any effect could raise the flag the
  // settle effect had already read "nowhere" from that same render and stopped
  // the video. A request is noted the moment the counter changes, in the render
  // that carries the new source.
  //
  // Time-boxed rather than tied to the counter, because a browser confirms its
  // own full screen later, on fullscreenchange — so "served" is not the same as
  // "arrived". The tick below re-renders when the grace runs out, which is what
  // lets a request that never lands fall through to "nowhere" instead of
  // stranding the video.
  const requestedAtRef = useRef(0);
  const seenWantedRef = useRef(fullscreenWanted);
  if (seenWantedRef.current !== fullscreenWanted) {
    seenWantedRef.current = fullscreenWanted;
    requestedAtRef.current = Date.now();
  }
  // Reaching full screen ends the grace for good. Without this, leaving again
  // within the window would still read as "arriving" — and an arriving video
  // is drawn, so a quick Back after starting a film would show the old docked
  // band for the rest of the three seconds.
  if (isFullscreen || multiviewActive) requestedAtRef.current = 0;
  const arriving =
    !isFullscreen &&
    !multiviewActive &&
    requestedAtRef.current > 0 &&
    Date.now() - requestedAtRef.current < ARRIVING_MS;
  const [, tick] = useState(0);
  useEffect(() => {
    if (!arriving) return;
    const expire = window.setTimeout(() => tick((n) => n + 1), ARRIVING_MS);
    return () => window.clearTimeout(expire);
  }, [arriving]);
  const placement = placementOf({
    playing: !!source,
    multiview: multiviewActive,
    fullscreen: isFullscreen,
    pip: pipActive,
    previewHost,
    arriving,
  });
  // A video with nowhere to be ends, on every device. This used to ASK for a
  // floating window first, which is why it was async and needed guarding
  // against re-entry while the request was in flight; there is nothing to wait
  // for now. Floating is only ever asked for deliberately — the ⧉ button, or
  // native on the way out of the app (see resolveNowhere).
  useEffect(() => {
    if (placement !== "nowhere") return;
    if (resolveNowhere() === "stop") close();
  }, [placement, close]);

  // Arriving AT the guide RECLAIMS the picture: the preview pane is one of the
  // three homes, so the floating window hands it back rather than leaving a
  // pane-sized hole with the picture still in the corner (owner, 2026-09-21).
  const wasPreviewHostRef = useRef(previewHost);
  useEffect(() => {
    const was = wasPreviewHostRef.current;
    wasPreviewHostRef.current = previewHost;
    if (was || !previewHost) return;
    if (pipActive && !isFullscreen && !multiviewActive) void exitPip();
  }, [previewHost, pipActive, isFullscreen, multiviewActive, exitPip]);

  // Coming back OUT of the floating window, with nothing else claiming the
  // picture, fills the screen (owner: "pip should expand to full screen").
  const wasPipRef = useRef(pipActive);
  useEffect(() => {
    const was = wasPipRef.current;
    wasPipRef.current = pipActive;
    if (!was || pipActive) return;
    const { source: playing, previewHost: host, requestFullscreen } = usePlayerStore.getState();
    if (afterPipCloses(!!playing, host) === "fill") requestFullscreen();
  }, [pipActive]);

  // A TV remote's media keys act on the player from any screen.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!usePlayerStore.getState().source) return;
      const { player, status } = usePlayerStore.getState();
      if (event.key === "MediaPlayPause") {
        event.preventDefault();
        void togglePlayPause();
      } else if (event.key === "MediaPlay" && status !== "playing") {
        event.preventDefault();
        void player?.play();
      } else if (event.key === "MediaPause" && status === "playing") {
        event.preventDefault();
        player?.pause();
      } else if (event.key === "MediaStop") {
        event.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [togglePlayPause, close]);

  // Transcoded VOD is a live-shaped pipe. mpegts.js reports duration: null for
  // it, and the mini-HLS path reports the CURRENT FRONTIER — how far ffmpeg
  // has transcribed so far — which grows over time (840s here after ~1 min of
  // a 94-min film). Neither is the movie length. Fall back to the
  // caller-supplied length (TMDB runtime): a fixed approximate length is far
  // better than a moving exact-looking one, which made the scale slide under
  // the thumb, progress read as nearly complete, and — with the frontier cap —
  // made it impossible to far-seek past what had been transcribed. Direct
  // (non-transcoded) VOD and live keep their transport-reported duration.
  // One rule shared with progress saving (player/duration.ts), so the bar and
  // Continue Watching's "% watched" can never disagree.
  const seekMax = Math.floor(effectiveDuration(source, duration));
  const playing = status === "playing";
  const fmt = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const previous = previousChannel();
  const currentName = source?.title;

  // Auto-hide the control bar after 10s of inactivity during playback, the
  // classic player behavior — it only reappears on interaction (mouse move /
  // click / key press). Paused or errored states keep it pinned so the user
  // always has transport access when playback has stopped.
  /** Set the moment the bar is put away, before React redraws: what closes with it must not refocus it. */
  const barPutAwayRef = useRef(false);

  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (usePlayerStore.getState().status !== "playing") return;
      // Never fade out from under an open menu.
      if (menuOpenRef.current) return;
      barPutAwayRef.current = true;
      setControlsVisible(false);
      setShowRecents(false);
      // Let go of a control that is fading out with the bar: kept, Up/Down
      // moved between invisible buttons instead of changing the channel, and
      // nothing on screen said where focus was (projector, 2026-09-26). With
      // nothing focused, Up/Down zap again and the next press brings the bar.
      const active = document.activeElement;
      if (active instanceof HTMLElement && surfaceRef.current?.contains(active) && surfaceRef.current.classList.contains("player-surface--fullscreen")) active.blur();
    }, 10_000);
  }, []);

  // Read inside the hide timer, which must not re-arm on every open/close.
  const menuOpenRef = useRef(menuOpen);
  menuOpenRef.current = menuOpen;

  // Back (a TV remote's, or the phone's) closes the menu before anything else,
  // through a history entry of its own.
  const openMenu = useCallback(() => setMenuOpen(true), []);
  // Closing hands focus back to the gear that opened it. Left on <body>, the
  // viewer could not see where they were after Back (full-screen sweep,
  // 2026-09-29).
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setTimeout(() => surfaceRef.current?.querySelector<HTMLElement>(".player-surface__gear")?.focus(), 0);
  }, []);
  // Registered on top of full screen, so Back closes the menu first.
  useBackLayer(menuOpen, closeMenu);
  // Closing the player closes its settings with it.
  useEffect(() => {
    if (!source && menuOpen) setMenuOpen(false);
  }, [source, menuOpen]);

  const controlsVisibleRef = useRef(controlsVisible);
  controlsVisibleRef.current = controlsVisible;

  // The Info button's panel in live full screen (FullscreenInfo). Back closes
  // it; a channel change shows the new channel's; leaving full screen drops it.
  const [info, setInfo] = useState<{ found: NowInfo | null } | null>(null);
  const infoOpenRef = useRef(false);
  infoOpenRef.current = info !== null;
  useBackLayer(info !== null, () => setInfo(null));
  /** The panel, with the bar put away and nothing focused: Up/Down still change the channel. */
  const openInfoPanel = useCallback(() => {
    barPutAwayRef.current = true;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    const active = document.activeElement;
    if (active instanceof HTMLElement && surfaceRef.current?.contains(active)) active.blur();
    setControlsVisible(false);
    setShowRecents(false);
    setInfo({ found: nowInfoFor(usePlayerStore.getState().source?.title ?? "") });
  }, []);

  const wakeControls = useCallback(() => {
    barPutAwayRef.current = false;
    setControlsVisible(true);
    scheduleHide();
  }, [scheduleHide]);

  // Any interaction with the surface (or a key press anywhere while a source
  // is active) brings the bar back; inactivity starts the hide countdown.
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;
    const events = ["mousemove", "mousedown", "touchstart", "click"] as const;
    for (const event of events) surface.addEventListener(event, wakeControls, { passive: true });
    const onKey = (event: KeyboardEvent) => {
      if (!usePlayerStore.getState().source) return;
      // Down from the full-screen bar — its bottom edge, nothing below — puts the
      // bar away and goes back to watching, where Up/Down change the channel. It
      // did nothing: a dead press, right where a viewer finishing with the
      // controls presses it (projector, 2026-09-27).
      const active = document.activeElement;
      const surface0 = surfaceRef.current;
      if (
        event.key === "ArrowDown" && !event.defaultPrevented &&
        surface0?.classList.contains("player-surface--fullscreen") &&
        active instanceof HTMLElement && active.closest(".player-surface__dock") && surface0.contains(active) &&
        // The timeline is the bar's top row in full screen: Down from it goes
        // to the buttons below (components/spatialNav.ts), not away.
        !active.classList.contains("player-surface__seek")
      ) {
        event.preventDefault();
        barPutAwayRef.current = true;
        active.blur();
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        setControlsVisible(false);
        setShowRecents(false);
        // Whatever closing the strip hands focus to afterwards, nothing hidden keeps it.
        setTimeout(() => {
          const now = document.activeElement;
          if (!controlsVisibleRef.current && now instanceof HTMLElement && surfaceRef.current?.contains(now)) now.blur();
        }, 50);
        return;
      }
      // OK puts the info panel away as it brings the bar up.
      if (event.key === "Enter" && infoOpenRef.current) setInfo(null);
      // Live TV full screen with the bar away: only OK brings it up (owner,
      // 2026-10-06: "should never start with toolbar open. only hitting the ok
      // button should open tool bar"). Up/Down change the channel (LivePage),
      // Left/Right and everything else leave the picture alone.
      if (
        event.key !== "Enter" &&
        !controlsVisibleRef.current &&
        surface0?.classList.contains("player-surface--fullscreen") &&
        usePlayerStore.getState().source?.type === "live"
      ) {
        return;
      }
      wakeControls();
      // OK, Left or Right in full screen with nothing focused: the bar comes up
      // with focus in it, on Play/Pause (owner, 2026-09-26: what opens takes
      // focus). OK did nothing, and Left/Right with the bar faded out only
      // woke it, so the bar was up with no control highlighted. Up/Down stay
      // the channel zap (LivePage).
      const surface = surfaceRef.current;
      // Up/Down open the bar too on a film or episode: there is no channel to
      // change, and they only woke the bar with nothing highlighted, a dead
      // press (audit, 2026-09-28). On live TV they stay the channel zap.
      const vertical = event.key === "ArrowUp" || event.key === "ArrowDown";
      // On live TV, Up/Down open it too while the bar is already up: with the
      // bar showing they move into it and never change the channel (owner,
      // 2026-10-05; the exceptions are in zapRule.ts).
      const opener =
        event.key === "Enter" || event.key === "ArrowLeft" || event.key === "ArrowRight" ||
        (vertical && (usePlayerStore.getState().source?.type !== "live" || arrowsBelongToBar(event)));
      if (opener && !event.defaultPrevented && document.activeElement === document.body && surface?.classList.contains("player-surface--fullscreen")) {
        const start = surface.querySelector<HTMLElement>("[data-nav-start]");
        if (start) {
          event.preventDefault();
          start.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    scheduleHide();
    return () => {
      for (const event of events) surface.removeEventListener(event, wakeControls);
      window.removeEventListener("keydown", onKey);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [wakeControls, scheduleHide]);

  // Left/Right in live full screen with the bar away: Left goes back to the
  // last channel watched, Right opens Recent channels (owner, 2026-10-06; the
  // remote's Recall and Recents actions). Neither may reach the app-wide D-pad
  // navigation, which put focus on the hidden bar's Pause and Multiview, so the
  // next Down only put the bar away instead of changing the channel (onn box,
  // 2026-10-06). Capture: before spatialNav. A viewer's own choice for the
  // button (Settings → Remote control) is taken earlier and never gets here.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (event.defaultPrevented || event.repeat) return;
      if (controlsVisibleRef.current || document.activeElement !== document.body) return;
      if (!surfaceRef.current?.classList.contains("player-surface--fullscreen")) return;
      if (usePlayerStore.getState().source?.type !== "live") return;
      event.preventDefault();
      runRemoteAction(event.key === "ArrowLeft" ? "lastChannel" : "recents");
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  // `f` toggles fullscreen from anywhere while a source is active — the dock
  // button alone is not enough: in the traditional layout the dock is a 42px
  // strip inside the preview pane that auto-hides during playback, so there
  // are stretches with no visible way to go fullscreen. Ignored while typing
  // (search box, provider form) and when a modifier is held, so it never
  // steals a browser shortcut or a character.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "f" && event.key !== "F") return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) return;
      if (!usePlayerStore.getState().source) return;
      event.preventDefault();
      toggleFullscreen();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleFullscreen]);

  // Pin the bar whenever playback is not actively playing (paused, buffering,
  // error, loading) and restart the countdown when playback resumes.
  //
  // Not while a live channel tunes in full screen: every zap passes through
  // loading/buffering, and the bar it pinned up turned the next Up/Down into
  // moves along the bar instead of channel changes (owner, 2026-10-06). Paused
  // and errors still pin it.
  const liveFullscreen = isFullscreen && source?.type === "live";
  useEffect(() => {
    const tuning = status === "idle" || status === "loading" || status === "buffering" || status === "retrying";
    if (status !== "playing" && !(liveFullscreen && tuning)) {
      barPutAwayRef.current = false;
      setControlsVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    } else if (status === "playing") {
      scheduleHide();
    }
  }, [status, scheduleHide, liveFullscreen]);

  // Into live full screen with the bar away, whatever brought it up on the way
  // (the OK that opened full screen woke it).
  useEffect(() => {
    if (!liveFullscreen) return;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setControlsVisible(false);
    setShowRecents(false);
    const active = document.activeElement;
    if (active instanceof HTMLElement && surfaceRef.current?.contains(active)) active.blur();
  }, [liveFullscreen]);

  const handlePrevious = useCallback(() => {
    const prev = previousChannel();
    if (prev) void tuneRecent(prev);
  }, [previousChannel, tuneRecent]);

  // The channel a full-screen zap is heading to, shown on the bar before it tunes.
  const [pendingZap, setPendingZap] = useState<string | null>(null);
  // A zap names the channel in a small banner, not by bringing up the bar: with
  // the bar up, Up/Down belong to it (zapRule.ts), so surfing stalled on the
  // second press (owner, 2026-10-06). The banner stays 3 s after the tune.
  const [zapBanner, setZapBanner] = useState(false);
  const zapBannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [zapName, setZapName] = useState<string | null>(null);
  const showZapBanner = useCallback((name: string) => {
    setZapName(name);
    setZapBanner(true);
    if (zapBannerTimer.current) clearTimeout(zapBannerTimer.current);
    zapBannerTimer.current = setTimeout(() => setZapBanner(false), 3_000);
  }, []);
  const handlePendingZap = useCallback((name: string | null) => {
    setPendingZap(name);
    if (name) showZapBanner(name);
  }, [showZapBanner]);
  // The Live page's own zap (full screen opened from the guide) names its channel here.
  useEffect(() => onZapName(showZapBanner), [showZapBanner]);
  useEffect(() => () => {
    if (zapBannerTimer.current) clearTimeout(zapBannerTimer.current);
  }, []);

  // What opens takes focus (owner, 2026-09-26): the recent-channels strip
  // opened from ▤ left focus on ▤, while the remote's Recents key moved it in.
  // Focused once it is DRAWN, not on a timer: a setTimeout(0) sometimes ran
  // before the strip was on screen, and Right in full screen opened it with
  // nothing focused (nav audit, onn box, 2026-10-10; a race the faster release
  // build happened to win).
  const focusStripRef = useRef(false);
  const toggleRecents = useCallback(() => {
    const open = !showRecents;
    setShowRecents(open);
    focusStripRef.current = open;
  }, [showRecents]);
  useEffect(() => {
    if (!showRecents) {
      focusStripRef.current = false;
      return;
    }
    if (!focusStripRef.current) return;
    let timer = 0;
    let tries = 0;
    const attempt = () => {
      const button = surfaceRef.current?.querySelector<HTMLElement>(".player-surface__recents button");
      if (button) {
        focusStripRef.current = false;
        button.focus();
      } else if (++tries < 20) {
        timer = window.setTimeout(attempt, 50);
      }
    };
    attempt();
    return () => window.clearTimeout(timer);
  }, [showRecents, controlsVisible]);

  // Back closes the strip first, and focus goes back to ▤ that opened it.
  // Back from inside the strip left full screen altogether (full-screen
  // sweep, 2026-09-29).
  const closeRecents = useCallback(() => {
    setShowRecents(false);
    // Not when the bar went away with the strip (Down from the bar): the ring
    // landed on the hidden ▤ (nav audit, onn box, 2026-10-06).
    setTimeout(() => {
      if (barPutAwayRef.current) return;
      surfaceRef.current?.querySelector<HTMLElement>('[aria-label="Recent channels"], [aria-label="Hide recent channels"]')?.focus();
    }, 0);
  }, []);
  useBackLayer(showRecents && controlsVisible, closeRecents);

  const handleTuneRecent = useCallback(
    (channel: RecentChannel) => {
      setShowRecents(false);
      void tuneRecent(channel);
    },
    [tuneRecent],
  );

  // Remote buttons mapped to the player's own controls (remote/remoteMap.ts)
  // run the same code as the buttons on the bar. Each says no (false) when it
  // does not apply, so the press is not swallowed silently.
  useRemoteActions({
    exitFullscreen: () => {
      if (!isFullscreen) return false;
      toggleFullscreen();
    },
    fullscreen: () => {
      if (!source) return false;
      if (isFullscreen) toggleFullscreen();
      else usePlayerStore.getState().requestFullscreen();
    },
    showBar: () => {
      if (!source) return false;
      wakeControls();
      if (isFullscreen) surfaceRef.current?.querySelector<HTMLElement>("[data-nav-start]")?.focus();
    },
    info: () => {
      // Beside the guide, the Live page's card answers instead.
      if (!isFullscreen) return false;
      // A film or episode: the bar names it.
      if (!isLive) {
        wakeControls();
        return;
      }
      // Live: the programme info panel, from the guide's listings. Info again closes it.
      setInfo((open) => (open ? null : { found: nowInfoFor(source?.title ?? "") }));
    },
    recents: () => {
      // Full screen only (owner, 2026-10-12): on the guide the strip opened inside the small preview
      // window and Back then left focus on the nav rail. Elsewhere the press does its own job.
      if (!isFullscreen || source?.type !== "live" || recentChannels.length === 0) return false;
      wakeControls();
      setShowRecents(true);
      // What opens takes focus (owner, 2026-09-26), once it is drawn (focusStripRef).
      focusStripRef.current = true;
      // Already open: no state changes, so go in now.
      if (showRecents) surfaceRef.current?.querySelector<HTMLElement>(".player-surface__recents button")?.focus();
    },
    playerSettings: () => {
      // Menu is the player's settings while watching full screen; anywhere else it
      // goes on to the app's Settings (useRemoteAppActions; owner, 2026-10-10).
      if (!source || !isFullscreen) return false;
      setMenuOpen(true);
    },
    multiview: () => {
      if (source?.type !== "live") return false;
      startMultiview();
    },
    pip: () => {
      if (!source) return false;
      if (!pipSupported) {
        showRemoteNote("Picture-in-picture isn't available on this TV");
        return;
      }
      void togglePip();
    },
    lastChannel: () => {
      const prev = previousChannel();
      if (!prev) return false;
      // Named like any other zap in full screen (the banner, not the bar).
      if (isFullscreen && isLive) showZapBanner(prev.name);
      handlePrevious();
    },
  });

  // Hide the recent strip when the playing channel changes (it would show a
  // stale "now" highlight) or when the dock closes.
  useEffect(() => {
    setShowRecents(false);
  }, [currentName]);
  // The info panel follows a channel change, and goes with full screen.
  useEffect(() => {
    setInfo((open) => (open ? { found: nowInfoFor(currentName ?? "") } : null));
  }, [currentName]);
  useEffect(() => {
    if (!isFullscreen || !isLive) setInfo(null);
  }, [isFullscreen, isLive]);

  // Picture-in-picture IS the player while it is floating: the page underneath
  // must not also draw a player band (owner 2026-09-21). Only the browser's PiP
  // renders the picture from this element off-screen; Android's is a whole-app
  // window, so there the surface stays.
  const floating = pipActive && !isFullscreen && !runsOnDevice();

  // The guide's preview is a PICTURE, not a player (owner, 2026-09-22): no
  // transport row over it in either layout. In the traditional layout the dock
  // ate a 42px strip of a pane that is already small; in the overlay layout it
  // drew a band across the backdrop the grid sits on. The guide already owns
  // every action they duplicated — a row tunes, OK opens the card, the card
  // watches — and full screen still carries the full toolbar.
  const inPreview = previewHost && !isFullscreen;

  // The preview window is a stop on the remote (owner, 2026-10-06): Right from
  // Home, Live TV, Movies or Series in the rail, Down from the categories and
  // Up from the top of the guide land on it (epg/guideFocus.ts); OK fills the
  // screen, Up goes to the categories, Down into the guide. Only in the
  // Traditional layout, where the preview is a window of its own (in Overlay
  // the picture is the backdrop the guide sits on), and only while something
  // plays in it, so a press never lands on an empty box.
  const guideLayout = useEpgSettings((s) => s.layout);
  const previewStop = Boolean(inPreview && source && guideLayout === "traditional");
  const onPreviewKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      // Full screen starts with nothing highlighted, as from Go Live: focus left
      // on the window would keep OK and the arrows from reaching the bar and the
      // channel zap. Back returns it here (components/focusReturn.ts).
      // Marked as the opener: Back puts focus back on the window (the full-screen
      // effect below; the guide stands aside for it, epg/guideFocus.ts).
      event.currentTarget.setAttribute("data-fs-opener", "");
      event.currentTarget.blur();
      toggleFullscreen();
    } else if (event.key === "ArrowLeft") {
      // Left: the rail's Live TV, level with the window. Left to the app's
      // navigation, the tall window measured nearest to the rail's collapse
      // arrow above it (onn box, 2026-10-06).
      const active = document.querySelector<HTMLElement>(".navrail .navrail__item--active");
      const box = event.currentTarget.getBoundingClientRect();
      const at = active?.getBoundingClientRect();
      if (active && at && at.bottom > box.top && at.top < box.bottom) {
        event.preventDefault();
        event.stopPropagation();
        active.focus();
      }
    } else if (event.key === "ArrowUp") {
      const categories = document.querySelector<HTMLElement>(".epg-toolbar select");
      if (categories) {
        event.preventDefault();
        event.stopPropagation();
        categories.focus();
      }
    }
  };

  return (
    <div
      ref={surfaceRef}
      className={`player-surface ${source ? "player-surface--active" : ""} ${controlsVisible ? "" : "player-surface--idle"} ${isFullscreen ? "player-surface--fullscreen" : ""} ${floating || placement === "nowhere" ? "player-surface--floating" : ""}`}
      {...(previewStop
        ? { tabIndex: 0, role: "button", "aria-label": "Preview window: OK for full screen", "data-preview-stop": "", onKeyDown: onPreviewKey }
        : {})}
    >
      <video
        ref={videoRef}
        className="player-surface__video"
        playsInline
        preload="auto"
        onDoubleClick={source ? toggleFullscreen : undefined}
      />
      {/* A stream that could not start says so in the middle of the picture, in
          plain words: it used to be a small grey line in the bar, cut off at
          "The stream coul…" (owner, 2026-10-07). Not in the guide's preview,
          which has its own (PreviewStatus); the free-tier and press-play cases
          have their own screens too. */}
      {source && status === "error" && error && !inPreview && error.code !== "exhausted" && error.code !== "autoplay" && (
        <div className="player-surface__failed" role="alert">
          <strong>{isLive ? "This channel couldn't start." : "This video couldn't start."}</strong>
          <span>{failureReason(error.code, error.message)}</span>
          <span className="player-surface__failed-hint">Press ↻ in the bar to try again.</span>
        </div>
      )}
      {source && (
        <>
        {/* Top-right corner in full screen: the clock. The list of recently
            watched titles under it went (owner, 2026-10-06: "redundant to the
            button that we have" — Recent channels on the bar). */}
        {/* Top-left, over the picture: the player's own settings. Comes and
            goes with the rest of the controls (owner: "the button only shows
            when the clock and other buttons show") — so in the guide's preview,
            where there is no toolbar, there is no gear either. */}
        {controlsVisible && !inPreview && (
          <button
            type="button"
            className="player-surface__gear"
            onClick={() => (menuOpen ? closeMenu() : openMenu())}
            aria-label="Player settings"
            aria-expanded={menuOpen}
            title="Player settings — aspect ratio, subtitles, audio, speed"
          >
            ⚙
          </button>
        )}
        {menuOpen && <PlayerSettingsMenu onClose={closeMenu} />}
        {isFullscreen && isLive && <FullscreenZap onPending={handlePendingZap} />}
        {isFullscreen && isLive && info && <FullscreenInfo info={info.found} channelName={currentName ?? ""} />}
        {isFullscreen && isLive && zapBanner && !controlsVisible && !info && (
          <ZapBanner name={zapName ?? source?.title ?? ""} />
        )}
        {isFullscreen && (
          <div className="player-surface__corner">
            <PlayerClock />
            <SleepRemaining />
          </div>
        )}
        {controlsVisible && showRecents && recentChannels.length > 0 && (
          <div className="player-surface__recents" role="region" aria-label="Recent channels">
            {recentChannels.map((channel) => {
              const isCurrent = channel.name === currentName;
              return (
                <button
                  key={channel.id}
                  className={`player-surface__recent ${isCurrent ? "player-surface__recent--current" : ""}`}
                  onClick={() => handleTuneRecent(channel)}
                  onKeyDown={ignoreHeldOk}
                  aria-label={`Watch ${channel.name}`}
                >
                  {channel.logoUrl ? (
                    <img className="player-surface__recent-logo" src={channel.logoUrl} alt="" loading="lazy" />
                  ) : (
                    <span className="player-surface__recent-logo player-surface__recent-logo--fallback">
                      {tileLetter(channel.name, "S")}
                    </span>
                  )}
                  <span className="player-surface__recent-name">{channel.name}</span>
                </button>
              );
            })}
          </div>
        )}
        {!inPreview && (
        <div className="player-surface__dock" role="region" aria-label="Now playing">
          <div className="player-surface__info">
            <span className="player-surface__title" title={pendingZap ?? source.title}>
              {pendingZap ?? source.title ?? "Now playing"}
            </span>
            <span className="player-surface__meta">
              {isLive ? <LiveBadge label={status === "playing" ? "LIVE" : status.toUpperCase()} /> : fmt(currentTime)}
              {isLive && shift.available && shift.offsetSeconds >= AT_LIVE_S && (
                <span className="player-surface__behind">−{formatBehind(shift.offsetSeconds)}</span>
              )}
              {mode && <span className="player-surface__mode">{mode}</span>}
            </span>
          </div>
          {!isLive && seekMax > 0 && (
            <input
              className="player-surface__seek"
              type="range"
              min={0}
              max={seekMax}
              value={Math.min(Math.floor(currentTime), seekMax)}
              aria-label="Seek"
              onChange={(event) => seek(Number(event.target.value))}
              // A range input steps ONE second per arrow: a minute back took
              // sixty presses on a remote (health check, 2026-09-27). Left and
              // Right jump instead, and repeat while held.
              onKeyDown={(event) => {
                if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
                event.preventDefault();
                const now = usePlayerStore.getState().currentTime;
                const to = event.key === "ArrowLeft" ? now - SEEK_BACK_S : now + SEEK_FORWARD_S;
                seek(Math.min(Math.max(0, to), seekMax));
              }}
            />
          )}
          {isLive && shift.available && (
            <input
              className="player-surface__seek player-surface__seek--live"
              type="range"
              min={0}
              max={Math.max(1, Math.floor(shift.behindSeconds + shift.offsetSeconds))}
              value={Math.floor(shift.behindSeconds)}
              aria-label="Rewind live TV"
              aria-valuetext={shift.offsetSeconds >= AT_LIVE_S ? `${formatBehind(shift.offsetSeconds)} behind live` : "Live"}
              onChange={(event) => {
                const saved = shift.behindSeconds + shift.offsetSeconds;
                player?.seekFromLiveEdge(Number(event.target.value) - saved);
              }}
              // Left and Right jump, as on a film's bar (seekSteps.ts), and say where they landed.
              onKeyDown={(event) => {
                if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
                event.preventDefault();
                showRemoteNote(shiftLiveBy(player, event.key === "ArrowLeft" ? -SEEK_BACK_S : SEEK_FORWARD_S));
              }}
            />
          )}
          {status === "error" && error && (error.code === "exhausted" || error.code === "autoplay" || inPreview) && (
            <span className="player-surface__error" title={error.message}>
              {error.message}
            </span>
          )}
          {recording.message && (
            <p className="player-surface__note" role="status">
              {recording.message}{" "}
              <button type="button" className="player-surface__note-dismiss" onClick={recording.dismissMessage}>
                Dismiss
              </button>
            </p>
          )}
          <div className="player-surface__controls">
                {status === "error" && (
                  <Button variant="ghost" onClick={() => void playSource(source)} aria-label="Retry stream">
                    ↻
                  </Button>
                )}
                {isLive && shift.available && (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() => showRemoteNote(shiftLiveBy(player, -SEEK_BACK_S))}
                      aria-label={`Rewind ${SEEK_BACK_S} seconds`}
                      title={`Back ${SEEK_BACK_S} s (${formatBehind(shift.behindSeconds)} saved before this)`}
                    >
                      ⏪
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => showRemoteNote(shiftLiveBy(player, SEEK_FORWARD_S))}
                      disabled={shift.offsetSeconds < AT_LIVE_S}
                      aria-label={`Fast forward ${SEEK_FORWARD_S} seconds`}
                      title={`Forward ${SEEK_FORWARD_S} s`}
                    >
                      ⏩
                    </Button>
                    {shift.offsetSeconds >= AT_LIVE_S && (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          player?.seekFromLiveEdge(0);
                          showRemoteNote("● Live");
                        }}
                        aria-label="Back to live"
                        title={`${formatBehind(shift.offsetSeconds)} behind live`}
                      >
                        ● LIVE
                      </Button>
                    )}
                  </>
                )}
                {recording.supported && (
                  <Button
                    variant={recording.recording ? "danger" : "ghost"}
                    onClick={() =>
                      void (recording.recording
                        ? recording.stop()
                        : // Prefer a real file the user picks whenever the
                          // browser offers a picker: it can live on an external
                          // drive, so the recording is not bounded by the OPFS
                          // quota and does not have to be exported afterwards.
                          // Falls back to OPFS where no picker exists (TV
                          // WebViews), which is why this is capability-driven
                          // rather than a desktop check.
                          recording.start({ external: recording.capabilities.canWriteExternalStorage }))
                    }
                    aria-label={recording.recording ? "Stop recording" : "Record"}
                    title={
                      recording.recording
                        ? `Recording${recording.sizeLabel ? ` — ${recording.sizeLabel}` : ""}`
                        : recording.capabilities.canWriteExternalStorage
                          ? "Record — choose where to save (an external drive works)"
                          : recording.headroomLabel
                            ? `Record to this device (${recording.headroomLabel} available)`
                            : "Record to this device"
                    }
                  >
                    {recording.recording ? `⏺ ${recording.sizeLabel ?? ""}`.trim() : "⏺"}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={handlePrevious}
                  onKeyDown={ignoreHeldOk}
                  disabled={!previous}
                  aria-label={previous ? `Previous channel: ${previous.name}` : "No previous channel"}
                  title={previous ? `Previous: ${previous.name}` : "No previous channel"}
                >
                  ⇄
                </Button>
                <Button
                  variant="ghost"
                  onClick={toggleRecents}
                  disabled={recentChannels.length === 0}
                  aria-label={showRecents ? "Hide recent channels" : "Recent channels"}
                  title="Recently watched"
                >
                  ▤
                </Button>
                <Button variant="ghost" onClick={() => void togglePlayPause()} aria-label={playing ? "Pause" : "Play"} data-nav-start="">
                  {playing ? "❚❚" : "▶"}
                </Button>
                {/* What is on, from the bar as well as the remote's Info button
                    (owner, 2026-10-06: "there is no program info button in the
                    toolbar"). The bar goes away for the panel. */}
                {isFullscreen && isLive && (
                  <Button variant="ghost" className="player-surface__info-btn" onClick={openInfoPanel} aria-label="Programme info" title="What is on now and next">
                    ⓘ
                  </Button>
                )}
                {/* NO full-screen toggle here (owner 2026-09-21). It sat right
                    beside Pause, so on a remote it was one mis-press away from
                    shrinking the picture, and with only three video modes (the
                    three places in the comment above) it added confusion
                    without adding a way anywhere. Playing something already
                    fills the screen — playSource raises fullscreenWanted — and
                    only the guide's in-pane preview stays out of it. Leaving is
                    Back (TV remote, via the full-screen history entry) or
                    Escape, and `f` still toggles both ways for a keyboard. */}
                {pipSupported && (
                  <Button
                    variant="ghost"
                    className="player-surface__pip"
                    onClick={() => void togglePip()}
                    aria-label={pipActive ? "Exit picture in picture" : "Picture in picture"}
                    title={pipActive ? "Exit picture in picture" : "Picture in picture — keeps playing over other apps"}
                  >
                    ⧉
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="player-surface__multiview"
                  onClick={startMultiview}
                  disabled={!isLive}
                  aria-label="Multiview"
                  title="Open multiview grid"
                >
                  ⊞
                </Button>
                <Button variant="ghost" onClick={close} aria-label="Close player">
                  ✕
                </Button>
          </div>
        </div>
        )}
        </>
      )}
      <UpNext />
    </div>
  );
}
